import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";
import { PassTemplateView, resolveText } from "@/components/events/PassTemplateView";

// Target export pixel width from physical size (cm/mm) at ~200 DPI, clamped.
export function exportWidthPx(template) {
  const w = Number(template.width) || 10;
  const per = template.unit === "mm" ? 25.4 : 2.54; // units per inch
  const px = Math.round((w / per) * 200);
  return Math.min(Math.max(px, 700), 2200);
}

// Renders PassTemplateView off-screen and captures it to a canvas.
export async function capturePassCanvas({ template, ctx, widthPx }) {
  const holder = document.createElement("div");
  Object.assign(holder.style, { position: "fixed", left: "-100000px", top: "0", zIndex: "-1", pointerEvents: "none" });
  document.body.appendChild(holder);
  const root = createRoot(holder);
  await new Promise((res) => {
    root.render(<PassTemplateView template={template} ctx={ctx} widthPx={widthPx} />);
    setTimeout(res, 120);
  });
  const imgs = Array.from(holder.querySelectorAll("img"));
  await Promise.all(
    imgs.map((im) => (im.complete && im.naturalWidth ? Promise.resolve() : new Promise((r) => { im.onload = r; im.onerror = r; })))
  );
  await new Promise((r) => setTimeout(r, 60));
  const h = widthPx * ((Number(template.height) || 100) / (Number(template.width) || 100));
  const canvas = await html2canvas(holder.firstChild, {
    scale: 1,
    useCORS: true,
    backgroundColor: "#ffffff",
    width: widthPx,
    height: Math.round(h),
    logging: false,
  });
  root.unmount();
  holder.remove();
  return canvas;
}

// Batch version: saare passes ek hi hidden mount me render karke capture karta hai —
// per-pass React mount + fixed waits nahi lagte, isliye bulk ZIP/PDF export kaafi fast hota hai.
export async function capturePassCanvases({ template, items, widthPx }) {
  const holder = document.createElement("div");
  Object.assign(holder.style, { position: "fixed", left: "-100000px", top: "0", zIndex: "-1", pointerEvents: "none" });
  document.body.appendChild(holder);
  const root = createRoot(holder);
  await new Promise((res) => {
    root.render(
      <>
        {items.map((it, i) => (
          <PassTemplateView key={i} template={template} ctx={it.ctx} widthPx={widthPx} />
        ))}
      </>
    );
    setTimeout(res, 120);
  });
  const imgs = Array.from(holder.querySelectorAll("img"));
  await Promise.all(
    imgs.map((im) => (im.complete && im.naturalWidth ? Promise.resolve() : new Promise((r) => { im.onload = r; im.onerror = r; })))
  );
  const h = Math.round(widthPx * ((Number(template.height) || 100) / (Number(template.width) || 100)));
  const out = [];
  for (const node of Array.from(holder.children)) {
    out.push(await html2canvas(node, { scale: 1, useCORS: true, backgroundColor: "#ffffff", width: widthPx, height: h, logging: false }));
  }
  root.unmount();
  holder.remove();
  return out;
}

// FAST pure-2D-canvas renderer (no html2canvas). Background image sirf ek baar decode hota hai,
// phir har pass par bas drawImage/fillText — isliye bade batch bahut tez banta hai.
function loadImg(src, cache) {
  if (!src) return Promise.resolve(null);
  if (cache && cache.has(src)) return cache.get(src);
  const p = new Promise((res) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => res(im);
    im.onerror = () => res(null);
    im.src = src;
  });
  if (cache) cache.set(src, p);
  return p;
}

function drawCover(c, img, dx, dy, dw, dh) {
  const ir = img.width / img.height, br = dw / dh;
  let sw, sh, sx, sy;
  if (ir > br) { sh = img.height; sw = sh * br; sx = (img.width - sw) / 2; sy = 0; }
  else { sw = img.width; sh = sw / br; sx = 0; sy = (img.height - sh) / 2; }
  c.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

export async function renderPassCanvases({ template, items, widthPx }) {
  const w = Number(template.width) || 100, h = Number(template.height) || 100;
  const heightPx = Math.round(widthPx * (h / w));
  const cache = new Map();
  const bg = await loadImg(template.image, cache);
  const els = template.elements || [];
  const out = [];
  for (const it of items) {
    const canvas = document.createElement("canvas");
    canvas.width = widthPx; canvas.height = heightPx;
    const c = canvas.getContext("2d");
    c.fillStyle = "#ffffff"; c.fillRect(0, 0, widthPx, heightPx);
    if (bg) c.drawImage(bg, 0, 0, widthPx, heightPx);
    for (const el of els) {
      const left = (el.x / 100) * widthPx, top = (el.y / 100) * heightPx;
      if (el.kind === "qr" || el.kind === "image") {
        const val = el.kind === "qr" ? (it.ctx.qrDataUrl || "") : (it.ctx.data?.[el.bind] || "");
        if (!val) continue;
        const img = await loadImg(val, cache);
        if (!img) continue;
        const s = (el.size / 100) * widthPx;
        drawCover(c, img, left - s / 2, top - s / 2, s, s);
      } else {
        const fontPx = (el.size / 100) * widthPx;
        c.fillStyle = el.color || "#111111";
        c.font = `${el.weight || "700"} ${fontPx}px sans-serif`;
        c.textAlign = el.align || "center";
        c.textBaseline = "middle";
        const tx = el.align === "left" ? left : el.align === "right" ? left : left;
        c.fillText(resolveText(el, it.ctx), tx, top);
      }
    }
    if (it.ctx.tag) {
      const fs = Math.round(widthPx * 0.035);
      c.font = `800 ${fs}px sans-serif`;
      const tw = c.measureText(it.ctx.tag).width;
      const padX = Math.round(widthPx * 0.02), padY = Math.round(widthPx * 0.008);
      const bw = tw + padX * 2, bh = fs + padY * 2;
      c.fillStyle = "#fbbf24";
      c.fillRect(widthPx - bw, 0, bw, bh);
      c.fillStyle = "#451a03";
      c.textAlign = "left";
      c.textBaseline = "middle";
      c.fillText(it.ctx.tag, widthPx - bw + padX, bh / 2 + 1);
    }
    out.push(canvas);
  }
  return out;
}
